import {
    Button,
    Form,
    Modal,
    Tabs,
    Upload,
    message,
    Alert,
    Table,
    Tooltip,
    Typography,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import styles from "./styles/BulkGeneration.module.css";
import { QurealViewer } from "qureal-editor";
import QurealViewerBox from "../qmade/creator/QurealViewerBox";
import { useMedia } from "react-use";
import CodeView from "../common/CodeView";
import { useSingleItem } from "../actions/app/items";
import { parseCSV, ValidationErrorList } from "./utils/helpers";
import { set, startCase } from "lodash";
import {
    UploadOutlined,
    LoadingOutlined,
    InfoCircleOutlined,
} from "@ant-design/icons";
import JSZip from "jszip";
import { useQurealActions } from "qureal-editor";
import log from "cslog";

function BulkGeneration({ trigger, slug, is_template, schema }) {
    const [open, setOpen] = useState(false);
    const {
        isSuccess,
        isLoading,
        data: dataPreset,
    } = useSingleItem(
        "qdb_creation_schema",
        `/${schema?.id}?fields=id,block_id,name`,
        !!schema?.id
    );

    const [prefill, setPrefill] = useState({});
    const { actionRef, download, share } = useQurealActions();
    const [status, setStatus] = useState({ download: false, share: false });
    const statusRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const isMobile = useMedia("(max-width: 700px)");

    const [url, setUrl] = useState(
        `https://show.qureal.com/${is_template ? "ts" : "s"}/${slug}`
    );

    const [current_record_index, setCurrentRecordIndex] = useState(0);
    const [bulkData, setBulkData] = useState({
        records: [],
        currentIndex: 0,
        images: [],
    });

    const handleBulkDownload = async () => {
        const records = bulkData.records;

        if (!records || records.length === 0) {
            message.error("No records to download");
            return;
        }

        setIsDownloading(true);
        setDownloadProgress(0);

        const totalRecords = records.length;
        const newImages = [];

        try {
            for (let i = 0; i < totalRecords; i++) {
                try {
                    setBulkData((prev) => ({
                        ...prev,
                        currentIndex: i,
                    }));

                    setPrefill({ [dataPreset.block_id]: records[i] });

                    await new Promise((res) => setTimeout(res, 500));

                    statusRef.current = null;
                    download();

                    let dataUrl = null;
                    let attempts = 0;
                    const maxAttempts = 700;

                    while (!dataUrl && attempts < maxAttempts) {
                        await new Promise((res) => setTimeout(res, 100));
                        dataUrl = statusRef.current?.dataUrl;
                        attempts++;
                    }

                    if (!dataUrl) {
                        throw new Error(`Timeout for record ${i + 1}`);
                    }

                    newImages.push({ dataUrl, record: records[i] });

                    const progress = Math.round(((i + 1) / totalRecords) * 100);
                    setDownloadProgress(progress);
                } catch (err) {
                    console.error(`Error in record ${i + 1}:`, err);
                    message.error(
                        `Failed to process record ${i + 1}: ${err.message}`
                    );
                }
            }

            setBulkData((prev) => ({
                ...prev,
                images: newImages,
            }));

            if (newImages.length > 0) {
                const zip = new JSZip();

                for (let i = 0; i < newImages.length; i++) {
                    const { dataUrl, record } = newImages[i];
                    const base64 = dataUrl.split(",")[1];
                    const mimeType = dataUrl.split(";")[0].split(":")[1];
                    const extension = mimeType.split("/")[1] || "png";

                    const fileName = record.name
                        ? `${record.name.replace(
                              /[^a-z0-9]/gi,
                              "_"
                          )}.${extension}`
                        : `record_${i + 1}.${extension}`;

                    zip.file(fileName, base64, { base64: true });
                }

                const zipBlob = await zip.generateAsync({ type: "blob" });

                const link = document.createElement("a");
                link.href = URL.createObjectURL(zipBlob);
                link.download = "bulk_images.zip";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                message.success(`Downloaded ${newImages.length} images`);
            } else {
                message.warning("No files to download");
            }
        } catch (error) {
            console.error("Download failed:", error);
            message.error("Failed to create ZIP");
        } finally {
            setIsDownloading(false);
            setDownloadProgress(0);
        }
    };

    return (
        <>
            <div onClick={() => setOpen(true)}>{trigger}</div>
            <Modal
                title="Bulk Generation"
                open={open}
                onCancel={() => setOpen(false)}
                width={1000}
                style={{ top: 20 }}
                styles={{ body: { height: "80vh", overflowY: "auto" } }}
            >
                <div className={styles.body_wrapper}>
                    <div className={styles.preview_wrapper}>
                        <div className={styles.wrapper}>
                            <QurealViewerBox
                                key={`${slug}-${current_record_index}`}
                                item={{ id: slug }}
                                slug_field="id"
                                preview={true}
                                is_template={is_template}
                                status={status}
                                setStatus={(data) => {
                                    if (data?.dataUrl) {
                                        console.log(
                                            "✅ Received dataUrl",
                                            data.dataUrl.substring(0, 50) +
                                                "..."
                                        );
                                    } else {
                                        console.log(
                                            "Status update without dataUrl",
                                            data
                                        );
                                    }
                                    statusRef.current = data;
                                    setStatus(data);
                                }}
                                actionRef={actionRef}
                                download={download}
                                share={share}
                                url={url}
                                height="450px"
                                width="500px"
                                isMobile={isMobile}
                                prefill={prefill}
                                loading={isDownloading}
                            />
                        </div>
                        <div
                            className={styles.actionButtons || ""}
                            style={{
                                marginTop: "10px",
                                display: "flex",
                                gap: "10px",
                            }}
                        >
                            <Button onClick={() => download()}>Download</Button>
                            <Button
                                type="primary"
                                onClick={handleBulkDownload}
                                loading={isDownloading}
                                disabled={
                                    !bulkData.records ||
                                    bulkData.records.length === 0
                                }
                            >
                                {isDownloading
                                    ? `Downloading (${downloadProgress}%)`
                                    : "Download All as ZIP"}
                            </Button>
                        </div>
                    </div>
                    <div className={styles.form_wrapper}>
                        {isSuccess && (
                            <BulkGenInputs
                                schema={schema}
                                current_record_index={current_record_index}
                                setCurrentRecordIndex={setCurrentRecordIndex}
                                onPrefill={(data) => {
                                    setPrefill({ [dataPreset.block_id]: data });
                                }}
                                onRecordsChange={(newRecords) => {
                                    setBulkData((prev) => ({
                                        ...prev,
                                        records: newRecords,
                                    }));
                                }}
                            />
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default BulkGeneration;

function BulkGenInputs({
    onPrefill,
    schema,
    onRecordsChange,
    current_record_index,
    setCurrentRecordIndex,
}) {
    return (
        <div>
            <Tabs
                defaultActiveKey="noai"
                type="card"
                items={[
                    {
                        label: "With AI",
                        key: "ai",
                        children: <></>,
                    },
                    {
                        label: "Without AI",
                        key: "noai",
                        children: (
                            <BulkGenWithouAI
                                schema={schema}
                                onPrefill={onPrefill}
                                onRecordsChange={onRecordsChange}
                                current_record_index={current_record_index}
                                setCurrentRecordIndex={setCurrentRecordIndex}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}

function BulkGenWithouAI({
    schema,
    onPrefill,
    onRecordsChange,
    current_record_index,
    setCurrentRecordIndex,
}) {
    const [csvData, setCsvData] = useState(null);
    const [zipFile, setZipFile] = useState(null);
    const [zipContents, setZipContents] = useState(null);
    const [isValidatingZip, setIsValidatingZip] = useState(false);
    const [showDataPreview, setShowDataPreview] = useState(false);
    const [validationStatus, setValidationStatus] = useState(null); // 'success', 'error', or null
    const [validationErrors, setValidationErrors] = useState([]);
    const [validationMessage, setValidationMessage] = useState("");

    const {
        isSuccess,
        isLoading,
        data: dataSchema,
    } = useSingleItem("qdb_creation_schema", `/${schema?.id}`, !!schema?.id);

    const [records, setRecords] = useState([]);

    log.d(schema, "schema in BulkGenWithouAI");

    useEffect(() => {
        const record = records?.[current_record_index];
        if (record) {
            onPrefill(record);
        }
    }, [current_record_index, records]);

    if (!isSuccess) return <div>Loading...</div>;

    const fields = Object.keys(dataSchema?.true_schema?.schema?.properties).map(
        (field_key) => {
            const field = dataSchema.true_schema.schema.properties[field_key];
            return {
                name: field_key,
                label: startCase(field_key),
                itype: field?.itype || "text",
            };
        }
    );
    const imageFields = fields.filter((field) => field.itype === "img");
    const hasImageFields = imageFields.length > 0;
    const multipleImageFields = imageFields.length > 1;

    log.d(records, "records in bulk");

    //just a tester
    const ImageTester = ({ record, imageFields }) => {
        if (!record) return null;

        return (
            <div
                style={{
                    marginTop: 20,
                    border: "1px solid #ddd",
                    padding: 10,
                    borderRadius: 4,
                }}
            >
                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {imageFields.map((field) => {
                        const imageData = record[field.name];
                        return (
                            <div
                                key={field.name}
                                style={{ textAlign: "center", margin: "10px" }}
                            >
                                <div>
                                    <strong>{field.name}</strong>
                                </div>
                                {imageData?.url ? (
                                    <>
                                        <img
                                            src={imageData.url}
                                            alt={field.name}
                                            style={{
                                                maxWidth: 200,
                                                maxHeight: 200,
                                                border: "1px solid #eee",
                                            }}
                                            onLoad={() =>
                                                console.log(
                                                    `✅ Image loaded for ${field.name}`
                                                )
                                            }
                                            onError={(e) =>
                                                console.error(
                                                    `❌ Failed to load image for ${field.name}`,
                                                    e
                                                )
                                            }
                                        />
                                        <div
                                            style={{
                                                marginTop: 5,
                                                fontSize: 12,
                                                wordBreak: "break-all",
                                            }}
                                        >
                                            {imageData.url.substring(0, 30)}...
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        style={{
                                            width: 200,
                                            height: 150,
                                            background: "#f5f5f5",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        No image URL
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    //handle for sample csv download
    const downloadSampleCsv = () => {
        const headers = fields.map((f) => f.name).sort();
        const csvContent = [
            headers.join(","),
            headers.map(() => "").join(","),
        ].join("\n");
        const blob = new Blob(["\uFEFF" + csvContent], {
            type: "text/csv;charset=utf-8",
        });
        const link = Object.assign(document.createElement("a"), {
            href: URL.createObjectURL(blob),
            download: "sample_data.csv",
        });
        link.click();
    };

    //handle for csv upload
    const handleCSVUpload = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;

            //check for missing fields in uploaded csv
            try {
                const lines = content.trim().split(/\r\n|\n/);
                const headers = lines[0].split(",").map((h) => h.trim());
                const requiredFields = fields.map((f) => f.name);
                const missingFields = requiredFields.filter(
                    (field) => !headers.includes(field)
                );
                const extraFields = headers.filter(
                    (header) =>
                        !requiredFields.includes(header) &&
                        header.trim().length > 0
                );

                // If there are missing fields, show specific error
                if (missingFields.length > 0) {
                    setValidationStatus("error");
                    setValidationErrors([
                        {
                            type: "error",
                            message: `Missing required columns in CSV: ${missingFields.join(
                                ", "
                            )}`,
                            details: `Your CSV should contain these columns: ${requiredFields.join(
                                ", "
                            )}`,
                        },
                    ]);
                    message.error(
                        `Your CSV is missing required columns: ${missingFields.join(
                            ", "
                        )}`
                    );
                    return;
                }

                // Now proceed with full parsing and validation
                const { data, errors } = parseCSV(
                    content,
                    fields,
                    dataSchema?.true_schema?.schema
                );

                if (errors.length > 0) {
                    setCsvData(null);
                    setValidationErrors(errors);
                    setValidationStatus("error");
                    setValidationMessage(
                        `Found ${errors.length} validation issues`
                    );
                    message.error(`Found ${errors.length} validation issues`);
                    return;
                }

                setCsvData(data);
                log.d(csvData, "data of csv");

                // Only show interim message if ZIP isn't uploaded yet
                if (hasImageFields && !zipContents) {
                    setValidationStatus("pending");
                    setValidationMessage(
                        `CSV with ${data.length} rows validated. Please upload a ZIP with images.`
                    );
                    message.success(`CSV validated. Please upload a ZIP file.`);
                } else {
                    validateFiles(data);

                    // Update records with proper image URLs if needed
                    let recordsToUse =
                        zipContents && zipContents.imageUrlMap
                            ? mapImageUrlsToRecords(
                                  data,
                                  zipContents.imageUrlMap
                              )
                            : data;

                    setRecords(recordsToUse);
                    if (onRecordsChange) onRecordsChange(recordsToUse);

                    // Auto-select first row for preview
                    if (recordsToUse.length > 0) {
                        setCurrentRecordIndex(0);
                        onPrefill(recordsToUse[0]);
                        console.log(
                            "Auto-selected first row:",
                            recordsToUse[0]
                        );
                    }
                }
            } catch (error) {
                // Handle general CSV parsing errors
                setCsvData(null);
                setValidationStatus("error");
                setValidationErrors([
                    {
                        type: "error",
                        message: "Failed to parse CSV file",
                        details: error.message,
                    },
                ]);
                message.error("Failed to parse CSV. Please check its format.");
            }
        };

        reader.onerror = () => {
            setCsvData(null);
            setValidationStatus("error");
            setValidationErrors([
                {
                    type: "error",
                    message: "Error reading the file",
                },
            ]);
            message.error("Failed to read the file");
        };

        reader.readAsText(file);
        return false;
    };

    // Helper function to map image URLs to records
    const mapImageUrlsToRecords = (data, imageUrlMap) => {
        return data.map((row) => {
            const updatedRow = { ...row };

            // Replace image filenames with URLs in image fields
            imageFields.forEach((field) => {
                const imagePath = row[field.name];
                if (imagePath && imageUrlMap[imagePath]) {
                    updatedRow[field.name] = {
                        url: imageUrlMap[imagePath],
                    };
                }
            });

            return updatedRow;
        });
    };

    //upload zip file
    const handleZipUpload = (file) => {
        if (
            file.type !== "application/zip" &&
            file.type !== "application/x-zip-compressed"
        ) {
            setValidationStatus("error");
            setValidationErrors([
                { type: "error", message: "Please upload a ZIP file" },
            ]);
            return false;
        }

        setZipFile(file);
        setIsValidatingZip(true);
        setValidationStatus("pending");
        setValidationMessage("Processing ZIP file...");

        // Use JSZip to extract and validate the ZIP
        const zip = new JSZip();
        const reader = new FileReader();

        reader.onload = async (e) => {
            try {
                const contents = await zip.loadAsync(e.target.result);
                const files = Object.keys(contents.files);
                const imageFiles = files.filter(
                    (name) =>
                        !contents.files[name].dir &&
                        /\.(jpe?g|png|gif|webp|svg)$/i.test(name)
                );

                if (imageFiles.length === 0) {
                    setValidationStatus("error");
                    setValidationErrors([
                        {
                            type: "error",
                            message:
                                "ZIP file does not contain any image files",
                        },
                    ]);
                    setIsValidatingZip(false);
                    return;
                }
                const imageUrlMap = {};

                // Process each image file and create blob URLs
                for (const imagePath of imageFiles) {
                    try {
                        const fileData = await contents.files[imagePath].async(
                            "blob"
                        );
                        const imageUrl = URL.createObjectURL(fileData);
                        imageUrlMap[imagePath] = imageUrl;
                        const fileName = imagePath.split("/").pop();
                        imageUrlMap[fileName] = imageUrl;

                        console.log(
                            `Created URL for ${imagePath}: ${imageUrl}`
                        );
                    } catch (error) {
                        console.error(
                            `Failed to process image ${imagePath}:`,
                            error
                        );
                    }
                }
                const zipData = {
                    files,
                    imageFiles,
                    contents,
                    imageUrlMap,
                };

                log.d(zipData, "zip file data");
                setZipContents(zipData);

                setIsValidatingZip(false);

                // Only show interim message if CSV isn't uploaded yet
                if (!csvData) {
                    setValidationStatus("pending");
                    setValidationMessage(
                        `ZIP with ${imageFiles.length} images validated. Please upload CSV data.`
                    );
                    message.success("ZIP validated. Please upload a CSV file.");
                } else {
                    // If CSV already uploaded, run full validation
                    validateFiles(csvData, zipData);
                    const updatedRecords = csvData.map((row) => {
                        const updatedRow = { ...row };

                        // Replace image filenames with URLs in image fields
                        imageFields.forEach((field) => {
                            const imagePath = row[field.name];
                            if (imagePath && imageUrlMap[imagePath]) {
                                updatedRow[field.name] = {
                                    url: imageUrlMap[imagePath],
                                };
                            }
                        });

                        return updatedRow;
                    });
                    setRecords(updatedRecords);
                    if (onRecordsChange) onRecordsChange(updatedRecords);
                    if (updatedRecords.length > 0) {
                        setCurrentRecordIndex(0);
                        onPrefill(updatedRecords[0]);
                        console.log(
                            "Auto-selected first row with image URLs:",
                            updatedRecords[0]
                        );
                    }
                }
            } catch (error) {
                setValidationStatus("error");
                setValidationErrors([
                    {
                        type: "error",
                        message: `Failed to parse ZIP file: ${error.message}`,
                    },
                ]);
                setIsValidatingZip(false);
            }
        };

        reader.readAsArrayBuffer(file);
        return false;
    };

    // Validation logic moved to a separate function
    const validateFiles = (
        csvDataToValidate = csvData,
        zipDataToValidate = zipContents
    ) => {
        if (!csvDataToValidate || (hasImageFields && !zipDataToValidate)) {
            return;
        }

        const errors = [];

        // Check if image fields in CSV have corresponding files in the ZIP
        if (hasImageFields && zipDataToValidate) {
            const imageFieldNames = imageFields.map((field) => field.name);
            let totalImageReferences = 0;
            let matchedImageReferences = 0;

            csvDataToValidate.forEach((row, rowIndex) => {
                imageFieldNames.forEach((fieldName) => {
                    const imagePath = row[fieldName];

                    if (imagePath) {
                        totalImageReferences++;

                        const imageExists =
                            zipDataToValidate.files[imagePath] ||
                            zipDataToValidate.imageFiles.some((file) =>
                                file.endsWith(imagePath)
                            );

                        if (imageExists) {
                            matchedImageReferences++;
                        } else {
                            errors.push({
                                type: "error",
                                message: `Row ${
                                    rowIndex + 1
                                }: Image '${imagePath}' for field '${fieldName}' not found in ZIP`,
                            });
                        }
                    }
                });
            });

            if (errors.length === 0) {
                setValidationStatus("success");
                setValidationMessage(
                    `Validation successful: ${
                        csvDataToValidate.length
                    } rows of data with ${
                        zipDataToValidate.imageFiles.length
                    } images. ${
                        totalImageReferences > 0
                            ? `All ${totalImageReferences} image references match files in the ZIP.`
                            : "No image references found in CSV."
                    }`
                );
                message.success("Validation successful!");
            } else {
                setValidationStatus("error");
                setValidationErrors(errors);
                setValidationMessage(
                    `Found ${errors.length} image references that don't match files in the ZIP.`
                );
            }
        } else {
            setValidationStatus("success");
            setValidationMessage(
                `Validation successful: ${csvDataToValidate.length} rows of data.`
            );
            message.success("Validation successful!");
        }
    };

    const previewColumns = fields.map((field) => ({
        title: field.label,
        dataIndex: field.name,
        key: field.name,
    }));

    return (
        <div className={styles.box}>
            <div className={styles.uploadSection}>
                {/* CSV Upload Section */}
                {!csvData ? (
                    <Upload.Dragger
                        beforeUpload={handleCSVUpload}
                        accept=".csv"
                        showUploadList={false}
                        className={styles.uploadDragger}
                    >
                        <p className="ant-upload-drag-icon">
                            <UploadOutlined />
                        </p>
                        <p className="ant-upload-text">
                            Drag & drop CSV file or click to browse
                        </p>
                        <p className="ant-upload-hint">
                            <Typography.Link
                                onClick={(e) => {
                                    e.stopPropagation();
                                    downloadSampleCsv();
                                }}
                            >
                                Download sample CSV
                            </Typography.Link>
                        </p>
                    </Upload.Dragger>
                ) : (
                    <div className={styles.fileUploaded}>
                        <div className={styles.fileInfo}>
                            <span className={styles.fileIcon}>✓ </span>
                            <Typography.Text strong>
                                CSV File Uploaded
                            </Typography.Text>

                            <Typography.Text type="secondary">
                                {" "}
                                {csvData.length} records
                            </Typography.Text>
                        </div>
                        <Button
                            size="small"
                            onClick={() => setCsvData(null)}
                            className={styles.replaceBtn}
                        >
                            Replace
                        </Button>
                    </div>
                )}

                {/* ZIP Upload Section */}
                {hasImageFields &&
                    (!zipContents ? (
                        <div style={{ marginTop: 20 }}>
                            <Upload.Dragger
                                beforeUpload={handleZipUpload}
                                accept=".zip"
                                showUploadList={false}
                                disabled={isValidatingZip}
                                className={styles.uploadDragger}
                            >
                                <p className="ant-upload-drag-icon">
                                    {isValidatingZip ? (
                                        <LoadingOutlined />
                                    ) : (
                                        <UploadOutlined />
                                    )}
                                </p>
                                <p className="ant-upload-text">
                                    Drag & drop ZIP file or click to browse
                                </p>
                                <p className="ant-upload-hint">
                                    Upload{" "}
                                    {multipleImageFields
                                        ? "images folder"
                                        : "image file"}{" "}
                                    as ZIP archive
                                </p>
                            </Upload.Dragger>
                        </div>
                    ) : (
                        <div className={styles.fileUploaded}>
                            <div className={styles.fileInfo}>
                                <span className={styles.fileIcon}>✓ </span>
                                <Typography.Text strong>
                                    ZIP File Uploaded
                                </Typography.Text>
                                <Typography.Text type="secondary">
                                    {zipContents.imageFiles.length} images
                                </Typography.Text>
                            </div>
                            <Button
                                size="small"
                                onClick={() => {
                                    setZipContents(null);
                                    setZipFile(null);
                                }}
                                className={styles.replaceBtn}
                            >
                                Replace
                            </Button>
                        </div>
                    ))}
            </div>

            {validationStatus === "error" && validationErrors.length > 0 && (
                <ValidationErrorList errors={validationErrors} />
            )}

            {validationStatus === "success" && (
                <Alert
                    message="Validation Completed Successfully"
                    description={validationMessage}
                    type="success"
                    showIcon
                    style={{ marginBottom: 20 }}
                />
            )}

            {validationStatus === "pending" && validationMessage && (
                <Alert
                    message="Validation In Progress"
                    description={validationMessage}
                    type="info"
                    showIcon
                    style={{ marginBottom: 20 }}
                />
            )}

            {records?.length > 0 && (
                <div className={styles.navigationControls}>
                    <Button
                        disabled={current_record_index === 0}
                        onClick={() => {
                            const newIndex = current_record_index - 1;
                            setCurrentRecordIndex(newIndex);
                            console.log(
                                "Navigating to previous record:",
                                records[newIndex]
                            );
                            onPrefill(records[newIndex]);
                        }}
                    >
                        Previous
                    </Button>
                    <Typography.Text style={{ margin: "0 12px" }}>
                        Record {current_record_index + 1} of {records.length}
                    </Typography.Text>
                    <Button
                        disabled={current_record_index === records.length - 1}
                        onClick={() => {
                            const newIndex = current_record_index + 1;
                            setCurrentRecordIndex(newIndex);
                            console.log(
                                "Navigating to next record:",
                                records[newIndex]
                            );
                            onPrefill(records[newIndex]);
                        }}
                    >
                        Next
                    </Button>
                    <Tooltip title="View current record data">
                        <Button
                            type="text"
                            icon={<InfoCircleOutlined />}
                            onClick={() => setShowDataPreview(!showDataPreview)}
                        />
                    </Tooltip>
                </div>
            )}
            {/* <CodeView language="json" code={JSON.stringify(schema, null, 2)} />
					<CodeView language="json" code={JSON.stringify(fields, null, 2)} />
					<CodeView
					language="json"
					code={JSON.stringify(dataSchema.examples, null, 2)}
					/> */}
        </div>
    );
}
